import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../../api/client';
import { usePermissions } from '../../../context/permissionsContext';
import { provinciaOptions } from '../../../constants/provinciasArgentina';

const { Title } = Typography;

const Localidades = () => {
  const { hasPermission } = usePermissions();
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroProvincia, setFiltroProvincia] = useState(undefined);
  const [filtrosDebounced, setFiltrosDebounced] = useState({ nombre: '', provincia: undefined });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const t = setTimeout(
      () =>
        setFiltrosDebounced({
          nombre: filtroNombre.trim(),
          provincia: filtroProvincia,
        }),
      350
    );
    return () => clearTimeout(t);
  }, [filtroNombre, filtroProvincia]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/localidades', {
        params: {
          page,
          limit,
          nombre: filtrosDebounced.nombre || undefined,
          provincia: filtrosDebounced.provincia || undefined,
        },
      });
      setData(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar localidades');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filtrosDebounced]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      nombre: row.nombre,
      provincia: row.provincia,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        nombre: values.nombre.trim(),
        provincia: values.provincia,
      };
      if (editing) {
        await api.put(`/localidades/${editing.id}`, payload);
        message.success('Localidad actualizada');
      } else {
        await api.post('/localidades', payload);
        message.success('Localidad creada');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const eliminar = (row) => {
    Modal.confirm({
      title: 'Eliminar localidad',
      content: `¿Eliminar "${row.nombre}" (${row.provincia})?`,
      okText: 'Eliminar',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/localidades/${row.id}`);
          message.success('Localidad eliminada');
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const columns = [
    { title: 'Localidad', dataIndex: 'nombre', ellipsis: true, minWidth: 160 },
    { title: 'Provincia', dataIndex: 'provincia', ellipsis: true, width: isNarrow ? 160 : 220 },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 96,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          {hasPermission('localidades', 'editar') && (
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
          {hasPermission('localidades', 'eliminar') && (
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => eliminar(row)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card bordered={false} style={{ borderRadius: 8 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Localidades
        </Title>
        {hasPermission('localidades', 'crear') && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nueva localidad
          </Button>
        )}
      </div>

      <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={8}>
          <Input
            allowClear
            placeholder="Filtrar por localidad"
            value={filtroNombre}
            onChange={(e) => {
              setFiltroNombre(e.target.value);
              setPage(1);
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={10}>
          <Select
            allowClear
            showSearch
            placeholder="Filtrar por provincia"
            options={provinciaOptions}
            value={filtroProvincia}
            onChange={(v) => {
              setFiltroProvincia(v);
              setPage(1);
            }}
            style={{ width: '100%' }}
            optionFilterProp="label"
          />
        </Col>
      </Row>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 480 }}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} localidades`,
          size: 'small',
        }}
      />

      <Modal
        title={editing ? 'Editar localidad' : 'Nueva localidad'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => void submit()}
        okText="Guardar"
        width={isNarrow ? 'calc(100vw - 16px)' : 480}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
          <Form.Item
            name="nombre"
            label="Localidad"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input placeholder="Ej: Rosario" />
          </Form.Item>
          <Form.Item
            name="provincia"
            label="Provincia"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select
              showSearch
              placeholder="Seleccionar provincia"
              options={provinciaOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Localidades;
